import { useEffect, useId, useState } from 'react';
import { testAdminMailConfig } from '../infrastructure/adminApi';

const inputClass = 'mt-2 w-full border border-[#383142] bg-[#0f0d14] px-3 py-2.5 text-sm text-[#f2edf5] outline-none transition focus:border-[#b66ee3] focus:ring-2 focus:ring-[#8e4bb5]/20';
const labelClass = 'block text-[10px] font-bold uppercase tracking-[.14em] text-[#afa2b9]';

const clone = (value) => JSON.parse(JSON.stringify(value));

function Field({ label, hint, children }) {
  const id = useId();
  return (
    <label className={labelClass} htmlFor={id}>
      {label}
      <span id={id} className="contents">{children}</span>
      {hint && <span className="mt-1 block text-[11px] font-normal normal-case tracking-normal text-[#817488]">{hint}</span>}
    </label>
  );
}

export default function SmtpEditor({ authorization, resource, onSave, onReset, isSaving, error, notice }) {
  const [draft, setDraft] = useState(() => ({ ...clone(resource.config), pass: '' }));
  const [testTo, setTestTo] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testMessage, setTestMessage] = useState('');

  useEffect(() => {
    setDraft({ ...clone(resource.config), pass: '' });
  }, [resource]);

  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const formattedUpdate = resource.meta.updatedAt
    ? new Date(resource.meta.updatedAt).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
    : 'Sin cambios guardados';

  const submitTest = async (event) => {
    event.preventDefault();
    setIsTesting(true);
    setTestMessage('');
    try {
      const payload = { ...draft };
      // Si no se escribió contraseña, el backend conserva la guardada para la prueba.
      const result = await testAdminMailConfig(authorization, testTo.trim(), payload);
      setTestMessage(`Correo de prueba enviado a ${result.to}. Revisa la bandeja y el spam.`);
    } catch (testError) {
      setTestMessage(`No se pudo enviar: ${testError.message}`);
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="mx-auto grid max-w-[1480px] gap-8 px-5 py-7 lg:grid-cols-[minmax(0,1fr)_minmax(360px,.82fr)] lg:px-8">
      <section className="min-w-0">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[#312a3a] pb-5">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[.2em] text-[#a887b9]">Módulo 02 · Acceso de jugadores</p>
            <h2 className="mt-1 font-display text-4xl uppercase text-[#f3edf6]">Correo SMTP</h2>
            <p className="mt-2 max-w-xl text-xs leading-5 text-[#8f8298]">
              Se usa para enviar códigos de activación y enlaces de acceso. Sin esto, los códigos
              solo quedan en los registros del servidor y nadie puede entrar.
            </p>
          </div>
          <div className="text-right font-mono text-[9px] leading-5 text-[#84768d]">
            <p>{resource.meta.source === 'defaults' ? 'Sin configurar (valores vacíos)' : `Versión ${resource.meta.version}`}</p>
            <p>{formattedUpdate}</p>
          </div>
        </div>

        <form
          onSubmit={(event) => { event.preventDefault(); onSave(draft); }}
          className="mt-7 border border-[#312a3a] bg-[#121019]/80 p-5 sm:p-6"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Host SMTP" hint="Ej. smtp.tudominio.com o smtp.gmail.com">
              <input
                value={draft.host || ''}
                onChange={(event) => update('host', event.target.value)}
                placeholder="smtp.ejemplo.com"
                maxLength={253}
                className={inputClass}
                autoComplete="off"
              />
            </Field>
            <Field label="Puerto" hint="587 = STARTTLS · 465 = SSL directo · 25/1025 = local">
              <input
                type="number"
                value={draft.port ?? 587}
                min={1}
                max={65535}
                onChange={(event) => update('port', Number(event.target.value))}
                className={inputClass}
              />
            </Field>
          </div>

          <label className="mt-5 flex cursor-pointer items-center gap-3 text-[10px] font-bold uppercase tracking-wider text-[#d4c8dc]">
            <input
              type="checkbox"
              checked={Boolean(draft.secure)}
              onChange={(event) => update('secure', event.target.checked)}
              className="h-5 w-5 accent-[#a65fd0]"
            />
            SSL directo (puerto 465)
            <span className="font-normal normal-case tracking-normal text-[#817488]">
              Apagado = STARTTLS (recomendado, puerto 587)
            </span>
          </label>

          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <Field label="Usuario SMTP" hint="Normalmente tu correo completo">
              <input
                value={draft.user || ''}
                onChange={(event) => update('user', event.target.value)}
                placeholder="juego@tudominio.com"
                maxLength={320}
                className={inputClass}
                autoComplete="username"
              />
            </Field>
            <Field
              label="Contraseña SMTP"
              hint={resource.config.hasPassword ? 'Hay una guardada (••••••). Vacío = conservar. Escribe para cambiarla.' : 'Se guarda en el servidor. Nunca se muestra.'}
            >
              <input
                type="password"
                value={draft.pass || ''}
                onChange={(event) => update('pass', event.target.value)}
                placeholder={resource.config.hasPassword ? '•••••• (conservar)' : 'Contraseña o App Password'}
                className={inputClass}
                autoComplete="new-password"
              />
            </Field>
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <Field label="Remitente (From)" hint="Debe ser un correo válido de tu dominio">
              <input
                value={draft.from || ''}
                onChange={(event) => update('from', event.target.value)}
                placeholder="noreply@tudominio.com"
                maxLength={320}
                className={inputClass}
                autoComplete="email"
              />
            </Field>
            <Field label="Nombre del remitente" hint="Aparece como nombre bonito en el correo">
              <input
                value={draft.fromName || ''}
                onChange={(event) => update('fromName', event.target.value)}
                placeholder="La Maldición Ñaca Ñaca"
                maxLength={80}
                className={inputClass}
              />
            </Field>
          </div>

          {(error || notice) && (
            <div
              className={`mt-5 border-l-2 px-4 py-3 text-sm ${error ? 'border-[#ef7f8f] bg-[#2b1219] text-[#ffd8de]' : 'border-[#7dd6ae] bg-[#0d251d] text-[#c8f6df]'}`}
              role="status"
            >
              {error || notice}
            </div>
          )}

          <div className="mt-6 flex flex-col-reverse gap-3 border-t border-[#312a3a] pt-5 sm:flex-row sm:justify-between">
            <button type="button" onClick={onReset} disabled={isSaving} className="px-3 py-3 text-xs font-bold text-[#baacbf] underline decoration-[#65546f] underline-offset-4 disabled:opacity-40">
              Borrar configuración
            </button>
            <button type="submit" disabled={isSaving} className="border border-[#d8b6ee] bg-[#754196] px-8 py-3.5 text-xs font-black uppercase tracking-[.15em] text-white shadow-[4px_4px_0_#050407] transition active:translate-x-0.5 active:translate-y-0.5 disabled:opacity-45">
              {isSaving ? 'Guardando…' : 'Guardar correo'}
            </button>
          </div>
        </form>
      </section>

      <aside className="min-w-0 lg:sticky lg:top-6 lg:self-start">
        <div className="border border-[#40354a] bg-[#0d0b11]/80 p-5 shadow-[10px_12px_0_#050407]">
          <p className="font-mono text-[9px] uppercase tracking-[.2em] text-[#a887b9]">Probar envío</p>
          <h3 className="mt-1 font-display text-2xl uppercase text-white">Enviar prueba</h3>
          <p className="mt-2 text-xs leading-5 text-[#817488]">
            Usa los valores del formulario sin guardar. Escribe un correo destino y comprueba que llega.
          </p>
          <form onSubmit={submitTest} className="mt-4">
            <label className={labelClass} htmlFor="smtp-test-to">Correo destino</label>
            <input
              id="smtp-test-to"
              type="email"
              required
              value={testTo}
              onChange={(event) => setTestTo(event.target.value)}
              placeholder="tu@correo.com"
              className={inputClass}
            />
            <button
              type="submit"
              disabled={isTesting || !testTo.trim()}
              className="mt-4 w-full border border-[#7EC54A] bg-[#1A4D2E] px-5 py-3 text-xs font-black uppercase tracking-[.15em] text-white transition active:translate-x-0.5 active:translate-y-0.5 disabled:opacity-45"
            >
              {isTesting ? 'Enviando…' : 'Probar envío'}
            </button>
          </form>
          {testMessage && (
            <p className="mt-4 border-l-2 border-[#7EC54A] bg-black/30 px-3 py-2 text-xs leading-5 text-[#d6f5e3]" role="status">
              {testMessage}
            </p>
          )}
          <div className="mt-5 border-t border-[#312a3a] pt-4 font-mono text-[10px] leading-5 text-[#84768d]">
            <p>Flujo de jugadores:</p>
            <p className="mt-1">1. Correo nuevo → pide nombre → guarda como no validado → envía código.</p>
            <p>2. Correo existente → envía URL de acceso + código.</p>
            <p>3. Código = 6 dígitos, 10 min, 5 intentos. URL = 30 min, un uso.</p>
          </div>
        </div>
      </aside>
    </div>
  );
}
